/**
 * Recommended actions, each with the reasoning that produced it.
 *
 * Some carry a command that sets the dashboard up to act on them. That button
 * is the only place in a reply where the user changes the screen deliberately
 * rather than as a side effect of asking, so it says what it will do and does
 * exactly that — one command, on click, nothing on render.
 */

import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { FiArrowRight } from "react-icons/fi";
import { handleLouCommand } from "../../louCommandBridge/commandRouter";

/**
 * `selection.set_period` appears in the block specification but is not a
 * command: the contract has `set_time_period`, and names its dates `start`
 * and `end`. Translated here rather than refused, because the intent is
 * unambiguous and the alternative is a dead button.
 */
const translate = ({ command, params = {} }) => {
  if (command !== "selection.set_period") return { command, params };
  const { start_date: start, end_date: end, ...rest } = params;
  return {
    command: "selection.set_time_period",
    params: { preset: "CUSTOM", ...(start ? { start } : {}), ...(end ? { end } : {}), ...rest },
  };
};

const PRIORITIES = ["High", "Medium", "Low"];

const RecommendationDeck = ({ recommendations = [] }) => {
  const dispatch = useDispatch();
  if (!recommendations.length) return null;

  const run = (action) => {
    const { command, params } = translate(action);
    dispatch(
      handleLouCommand({
        command,
        params,
        metadata: { version: "1.8", command_id: `rec_${Date.now()}` },
      }),
    );
  };

  return (
    <section className="lou-recs" aria-label="Recommended actions">
      {recommendations.map((rec, i) => {
        const priority = PRIORITIES.includes(rec.priority) ? rec.priority : "Medium";
        return (
          <article className="lou-recs__card" key={`${rec.title}-${i}`}>
            <header className="lou-recs__head">
              <div className="lou-recs__heading">
                <h4 className="lou-recs__title">{rec.title}</h4>
                {rec.type ? (
                  <span className="lou-recs__type">{rec.type}</span>
                ) : null}
              </div>
              <span
                className={`lou-recs__priority lou-recs__priority--${priority.toLowerCase()}`}
              >
                {priority}
              </span>
            </header>

            {rec.rationale ? (
              <p className="lou-recs__rationale">{rec.rationale}</p>
            ) : null}

            {rec.actionCommand?.command ? (
              <button
                type="button"
                className="lou-recs__action"
                onClick={() => run(rec.actionCommand)}
              >
                <span>{rec.actionCommand.label || "Show me"}</span>
                <FiArrowRight size={14} aria-hidden="true" />
              </button>
            ) : null}
          </article>
        );
      })}
    </section>
  );
};

RecommendationDeck.propTypes = { recommendations: PropTypes.array };

export default RecommendationDeck;
